import { apiClient } from '@shared/utils/api-client';
import type { CustomSet, CreateCustomSetDto, UpdateCustomSetDto } from '../types';
import type { ProductCardResponse } from '@modules/products';

export const customSetsApi = {
  getAll: async (): Promise<CustomSet[]> => {
    return apiClient.get<CustomSet[]>('/custom-sets');
  },

  getOne: async (id: number): Promise<CustomSet> => {
    return apiClient.get<CustomSet>(`/custom-sets/${id}`);
  },

  getProducts: async (
    id: number,
    filters?: {
      categoryId?: number;
      colorIds?: number[];
      flowerTypeIds?: number[];
      assemblyTypeIds?: number[];
      flowerCounts?: number[];
      minPrice?: number;
      maxPrice?: number;
      sortBy?: string;
    },
  ): Promise<ProductCardResponse[]> => {
    if (!filters) {
      return apiClient.get<ProductCardResponse[]>(`/custom-sets/${id}/products`);
    }
    const params = new URLSearchParams();
    if (filters.categoryId) params.append('categoryId', filters.categoryId.toString());
    if (filters.colorIds && filters.colorIds.length > 0) {
      filters.colorIds.forEach(id => params.append('colorIds', id.toString()));
    }
    if (filters.flowerTypeIds && filters.flowerTypeIds.length > 0) {
      filters.flowerTypeIds.forEach(id => params.append('flowerTypeIds', id.toString()));
    }
    if (filters.assemblyTypeIds && filters.assemblyTypeIds.length > 0) {
      filters.assemblyTypeIds.forEach(id => params.append('assemblyTypeIds', id.toString()));
    }
    if (filters.flowerCounts && filters.flowerCounts.length > 0) {
      filters.flowerCounts.forEach(count => params.append('flowerCounts', count.toString()));
    }
    if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    const queryString = params.toString();
    const url = queryString ? `/custom-sets/${id}/products?${queryString}` : `/custom-sets/${id}/products`;
    return apiClient.get<ProductCardResponse[]>(url);
  },

  create: async (data: CreateCustomSetDto, file?: File): Promise<CustomSet> => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) {
      formData.append('description', data.description);
    }
    // Добавляем productIds только если они есть
    if (data.productIds && data.productIds.length > 0) {
      data.productIds.forEach((productId) => {
        formData.append('productIds', productId.toString());
      });
    }
    if (data.imageUrl) {
      formData.append('imageUrl', data.imageUrl);
    }
    if (file) {
      formData.append('image', file);
    }
    return apiClient.post<CustomSet>('/custom-sets', formData);
  },

  update: async (id: number, data: UpdateCustomSetDto, file?: File): Promise<CustomSet> => {
    const formData = new FormData();
    if (data.name !== undefined) {
      formData.append('name', data.name);
    }
    if (data.description !== undefined) {
      formData.append('description', data.description || '');
    }
    if (data.imageUrl) {
      formData.append('imageUrl', data.imageUrl);
    }
    if (data.shouldDeleteImage !== undefined) {
      formData.append('shouldDeleteImage', String(data.shouldDeleteImage));
    }
    if (file) {
      formData.append('image', file);
    }
    return apiClient.put<CustomSet>(`/custom-sets/${id}`, formData);
  },

  addProducts: async (id: number, productIds: number[]): Promise<CustomSet> => {
    return apiClient.patch<CustomSet>(`/custom-sets/${id}/products`, {
      productIds,
    });
  },

  removeProducts: async (id: number, productIds: number[]): Promise<CustomSet> => {
    return apiClient.delete<CustomSet>(`/custom-sets/${id}/products`, {
      productIds,
    });
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/custom-sets/${id}`);
  },
};

